import {z} from "zod/v4-mini"
import {
    registerTemplateGraphNode,
    getTemplateNodeReturns,
    numberLike,
    NumberInput,
    MaterialAssignments,
    TypeDescriptors,
    GeometryGraphMesh,
    MeshGeometryAttributeSetLambda,
    MeshExport,
    TemplateNode,
    visitNone,
} from "@cm/template-nodes"
import type {
    TemplateNodeDefinition,
    OnCompileContext,
    TemplateNodeContext,
    VisitorNodeResult,
    NumberLike,
    CurveLikeNode,
    BuilderInlet,
    BuilderOutlet,
    GraphBuilderScope,
    NodeEvaluator,
    EvaluatedTemplateInput,
    EvaluatedTemplateInputs,
    MaterialAssignment,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import type {
    MattressMeshData,
    MattressMeshGeometryParameterKey,
    MattressMeshGeometryParametersLocalNodes,
    MattressMeshBaseMeshLocalNodes,
    MattressMeshSegmentedMeshLocalNodes,
    MattressCutData,
    MattressMaterialAssignmentData,
    MattressNodePath,
    MattressMeshGeometryParametersData,
} from "#mattress/nodes/component/mattress-component-types"
import type {MattressMeshSegmentationInfo} from "#mattress/geometry/mattress-mesh-segmentation"
import {
    getParametersForBaseMeshAttributesLocalNode,
    getParametersForSegmentedMeshAttributesLocalNode,
    MATTRESS_LOCAL_NODE_FOR_BASE_MESH,
    MATTRESS_LOCAL_NODE_FOR_SEGMENTED_MESH,
} from "#mattress/geometry/mattress-mesh-segmentation"
import {mattressFabricLayoutToTexUVLayout, getMattressCompileContext} from "#mattress/nodes/component/mattress-component-helpers"

import {IsNonNull, hashObject, uuid4} from "@cm/utils"
import {Matrix4} from "@cm/math"
import type {Evaluator} from "@cm/graph-v3"

const TD = TypeDescriptors

const meshGeometryParametersDesc = TD.ShallowJSON<MattressMeshGeometryParametersData>()
const geometryParameterSourceNodesDesc = TD.ShallowJSON<{[localId: string]: TemplateNode}>()

const SegmentationInfoTypeDescriptor: TypeDescriptors.Desc<MattressMeshSegmentationInfo> = {
    deepCompare: (a, b) => {
        if (!a || !b) return a === b
        return a.changeDetectionHash === b.changeDetectionHash
    },
    deepCopy: (a) => a,
}

const baseMaterialID = 0
const getMaterialIDForCutIdx = (cutIdx: number): number => cutIdx + 1

export {MATTRESS_LOCAL_NODE_FOR_BASE_MESH, MATTRESS_LOCAL_NODE_FOR_SEGMENTED_MESH} from "#mattress/geometry/mattress-mesh-segmentation"

export type MattressMeshGeometryParameters = {
    width: NumberLike
    length: NumberLike
    height: NumberLike
    cornerRadius: NumberLike
    topRadius: NumberLike
    bottomRadius: NumberLike
    cornerRadiusSegments: NumberLike
    topRadiusSegments: NumberLike
    bottomRadiusSegments: NumberLike
    widthSegments: NumberLike
    heightSegments: NumberLike
    lengthSegments: NumberLike
    resolutionMultiplier: NumberLike
}

const mattressMeshGeometryParameters = z.object({
    width: numberLike,
    length: numberLike,
    height: numberLike,
    cornerRadius: numberLike,
    topRadius: numberLike,
    bottomRadius: numberLike,
    cornerRadiusSegments: numberLike,
    topRadiusSegments: numberLike,
    bottomRadiusSegments: numberLike,
    widthSegments: numberLike,
    heightSegments: numberLike,
    lengthSegments: numberLike,
    resolutionMultiplier: numberLike,
})

export type MattressMeshParameters = {
    name: string
    geometry: MattressMeshGeometryParameters
}

const definition: TemplateNodeDefinition<MattressMeshParameters> = {
    nodeClass: "MattressMesh",
    validation: {
        parameters: z.object({
            name: z.string(),
            geometry: mattressMeshGeometryParameters,
        }),
        returns: getTemplateNodeReturns(),
    },
}

function localNodeForGeometryParameter(scope: GraphBuilderScope, parameterName: MattressMeshGeometryParameterKey): BuilderOutlet<NumberInput> {
    return scope.const(
        () =>
            new NumberInput({
                parameters: {
                    name: getLocalNodeIdForGeometryParameter(parameterName),
                    id: getLocalNodeIdForGeometryParameter(parameterName),
                },
            }),
        getLocalNodeIdForGeometryParameter(parameterName),
    )
}

@registerTemplateGraphNode
export class MattressMesh extends MattressNode<MattressMeshParameters> {
    public override get definition() {
        return definition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressMeshParameters> {
        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {componentDepth} = mattressContext

        if (componentDepth !== 1) throw new Error("MattressMesh node currently not allowed in nested component")

        const {
            width,
            length,
            height,
            cornerRadius,
            topRadius,
            bottomRadius,
            bottomRadiusSegments,
            topRadiusSegments,
            cornerRadiusSegments,
            widthSegments,
            lengthSegments,
            heightSegments,
            resolutionMultiplier,
        } = this.parameters.geometry

        const scope = evaluator.getScope(this)

        const meshGeometryParametersData = scope.pureLambda(
            scope.tuple(
                evaluator.evaluate(width),
                evaluator.evaluate(length),
                evaluator.evaluate(height),
                evaluator.evaluate(cornerRadius),
                evaluator.evaluate(topRadius),
                evaluator.evaluate(bottomRadius),
                evaluator.evaluate(bottomRadiusSegments),
                evaluator.evaluate(topRadiusSegments),
                evaluator.evaluate(cornerRadiusSegments),
                evaluator.evaluate(widthSegments),
                evaluator.evaluate(lengthSegments),
                evaluator.evaluate(heightSegments),
                evaluator.evaluate(resolutionMultiplier),
            ),
            ([
                width,
                length,
                height,
                cornerRadius,
                topRadius,
                bottomRadius,
                bottomRadiusSegments,
                topRadiusSegments,
                cornerRadiusSegments,
                widthSegments,
                lengthSegments,
                heightSegments,
                resolutionMultiplier,
            ]) => {
                const parametersReady = ![
                    width,
                    length,
                    height,
                    cornerRadius,
                    topRadius,
                    bottomRadius,
                    bottomRadiusSegments,
                    topRadiusSegments,
                    cornerRadiusSegments,
                    widthSegments,
                    lengthSegments,
                    heightSegments,
                    resolutionMultiplier,
                ].some((n) => n === null)
                const eps = 1e-2

                return {
                    parametersReady,
                    parameters: {
                        width: width ?? eps,
                        length: length ?? eps,
                        height: height ?? eps,
                        cornerRadius: cornerRadius ?? eps,
                        topRadius: topRadius ?? eps,
                        bottomRadius: bottomRadius ?? eps,
                        bottomRadiusSegments: bottomRadiusSegments ?? 1,
                        topRadiusSegments: topRadiusSegments ?? 1,
                        cornerRadiusSegments: cornerRadiusSegments ?? 1,
                        widthSegments: widthSegments ?? 1,
                        lengthSegments: lengthSegments ?? 1,
                        heightSegments: heightSegments ?? 1,
                        resolutionMultiplier: resolutionMultiplier ?? 1,
                    },
                }
            },
            "meshGeometryParametersData",
        )

        const meshGeometryParametersReady = scope.get(meshGeometryParametersData, "parametersReady")

        const meshGeometryParameters = scope.value(
            scope.holdLast<MattressMeshGeometryParametersData>(scope.branch(scope.get(meshGeometryParametersData, "parameters"))[0]),
            meshGeometryParametersDesc,
            "meshGeometryParameters",
        )

        const meshGeometryParametersLocalNodes = scope.struct<MattressMeshGeometryParametersLocalNodes>("geometryParametersLocalNodes", {
            width: localNodeForGeometryParameter(scope, "width"),
            length: localNodeForGeometryParameter(scope, "length"),
            height: localNodeForGeometryParameter(scope, "height"),
            cornerRadius: localNodeForGeometryParameter(scope, "cornerRadius"),
            topRadius: localNodeForGeometryParameter(scope, "topRadius"),
            bottomRadius: localNodeForGeometryParameter(scope, "bottomRadius"),
            cornerRadiusSegments: localNodeForGeometryParameter(scope, "cornerRadiusSegments"),
            topRadiusSegments: localNodeForGeometryParameter(scope, "topRadiusSegments"),
            bottomRadiusSegments: localNodeForGeometryParameter(scope, "bottomRadiusSegments"),
            widthSegments: localNodeForGeometryParameter(scope, "widthSegments"),
            lengthSegments: localNodeForGeometryParameter(scope, "lengthSegments"),
            heightSegments: localNodeForGeometryParameter(scope, "heightSegments"),
            resolutionMultiplier: localNodeForGeometryParameter(scope, "resolutionMultiplier"),
        })

        const baseMeshLocalNodesSrc = scope.gate(
            scope.const<MattressMeshBaseMeshLocalNodes>(
                () => ({
                    attributes: new MeshGeometryAttributeSetLambda({
                        parameters: {
                            name: "",
                            args: null,
                            generator: null,
                        },
                    }),
                    mesh: new GeometryGraphMesh({
                        parameters: {
                            name: MATTRESS_LOCAL_NODE_FOR_BASE_MESH,
                            meshAttributeSet: null,
                            materialAssignments: new MaterialAssignments({parameters: {}}),
                            materialSlotNames: {},
                            lockedTransform: Matrix4.identity().toArray(),
                            visible: false,
                            visibleDirectly: true,
                            visibleInReflections: true,
                            visibleInRefractions: true,
                            castRealtimeShadows: true,
                            receiveRealtimeShadows: true,
                        },
                    }),
                    export: new MeshExport({
                        parameters: {
                            name: "",
                            node: null,
                            id: uuid4(),
                        },
                    }),
                }),
                "baseMeshLocalNodesSrc",
            ),
            meshGeometryParametersReady,
        )

        const baseMeshLocalNodes = scope.pureLambda(
            scope.tuple(meshGeometryParametersLocalNodes, baseMeshLocalNodesSrc),
            ([meshGeometryParametersLocalNodes, baseMeshLocalNodesSrc]) => {
                if (!meshGeometryParametersLocalNodes || !baseMeshLocalNodesSrc) return null

                const attributes = baseMeshLocalNodesSrc.attributes.clone({
                    cloneInstanceIds: true,
                    cloneSubNode: () => false,
                    parameterOverrides: {
                        ...getParametersForBaseMeshAttributesLocalNode(meshGeometryParametersLocalNodes),
                    },
                })

                const mesh = baseMeshLocalNodesSrc.mesh.clone({
                    cloneInstanceIds: true,
                    cloneSubNode: () => false,
                    parameterOverrides: {
                        meshAttributeSet: attributes,
                    },
                })

                const exportNode = baseMeshLocalNodesSrc.export.clone({
                    cloneInstanceIds: true,
                    cloneSubNode: () => false,
                    parameterOverrides: {
                        node: mesh,
                    },
                })

                return {attributes, mesh, export: exportNode}
            },
            "baseMeshLocalNodes",
        )

        const geometryParameterSourceNodes: {[localId: string]: TemplateNode} = {}
        for (const [key, sourceNode] of Object.entries(this.parameters.geometry) as [MattressMeshGeometryParameterKey, NumberLike][]) {
            if (sourceNode instanceof TemplateNode) {
                geometryParameterSourceNodes[getLocalNodeIdForGeometryParameter(key)] = sourceNode
            }
        }

        aliasMattressMeshData(
            evaluator,
            scope.struct<MattressMeshData>("mattressMeshData", {
                geometryParameters: scope.phi(meshGeometryParameters, null),
                geometryParametersLocalNodes: scope.phi(meshGeometryParametersLocalNodes, null),
                geometryParameterSourceNodes: scope.value(geometryParameterSourceNodes, geometryParameterSourceNodesDesc, "geometryParameterSourceNodes"),
                baseMeshLocalNodes: scope.phi(baseMeshLocalNodes, null),
                segmentedMeshLocalNodes: null,
                segmentedMeshMaterialSlotsOwners: null,
                segmentedMeshMaterialAssignments: null,
            }),
        )

        return visitNone(this.parameters)
    }
}

// Aliases the mattress mesh data under a fixed key in the template scope; consumers (e.g. component
// graphs nested inside the Mattress) resolve it via `evaluatePartialMattressMeshData`.
export function aliasMattressMeshData(evaluator: NodeEvaluator, data: BuilderInlet<MattressMeshData | null>): void {
    evaluator.templateScope.alias(data, "mattressMeshData")
}

// Resolves the mattress mesh data published by `MattressMesh.onCompile`.
export function evaluatePartialMattressMeshData(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderOutlet<MattressMeshData | null> {
    return scope.unresolvedToNull(evaluator.templateScope.resolve<MattressMeshData>("mattressMeshData"))
}

function getLocalNodeIdForGeometryParameter(parameterName: MattressMeshGeometryParameterKey): string {
    return `meshGeometryParameterLocalNode_${parameterName}`
}

// Flattens all local nodes living in the mattress mesh data (the geometry-parameter inputs, base-mesh
// + segmented-mesh local nodes, and per-cut material-assignment inputs) into a single list, so the
// surrounding mattress component-instance can register them on its dummy template instance.
export function parseLocalNodesFromMattressMeshData(
    scope: GraphBuilderScope,
    mattressMeshData: BuilderInlet<MattressMeshData | null>,
): BuilderOutlet<TemplateNode[]> {
    const [mattressMeshDataValid] = scope.branch(mattressMeshData)
    const [geometryParametersLocalNodes] = scope.branch(scope.get(mattressMeshDataValid, "geometryParametersLocalNodes"))
    const [baseMeshLocalNodes] = scope.branch(scope.get(mattressMeshDataValid, "baseMeshLocalNodes"))
    const [segmentedMeshLocalNodes] = scope.branch(scope.get(mattressMeshDataValid, "segmentedMeshLocalNodes"))
    const [segmentedMeshMaterialAssignments] = scope.branch(scope.get(mattressMeshDataValid, "segmentedMeshMaterialAssignments"))

    return scope.pureLambda(
        scope.tuple(geometryParametersLocalNodes, baseMeshLocalNodes, segmentedMeshLocalNodes, segmentedMeshMaterialAssignments),
        ([geometryParametersLocalNodes, baseMeshLocalNodes, segmentedMeshLocalNodes, segmentedMeshMaterialAssignments]) => [
            ...Object.values(geometryParametersLocalNodes),
            ...Object.values(baseMeshLocalNodes),
            ...Object.values(segmentedMeshLocalNodes),
            ...Object.values(segmentedMeshMaterialAssignments)
                .filter(IsNonNull)
                .map((assignmentData) => assignmentData.localMaterialInput),
        ],
        "parseLocalNodesDataFromMattressMeshData",
    )
}

// Builds the per-local-node input map fed into the dummy template instance: geometry-parameter
// number values keyed by `getLocalNodeIdForGeometryParameter(...)`, plus material-graph data keyed
// by each material-assignment's local-material-input id.
export function getLocalInstanceInputsDataFromMattressMeshData(
    scope: GraphBuilderScope,
    mattressMeshData: BuilderInlet<MattressMeshData | null>,
): BuilderInlet<EvaluatedTemplateInputs> {
    const [mattressMeshDataValid] = scope.branch(mattressMeshData)
    const [geometryParametersDataValid] = scope.branch(scope.get(mattressMeshDataValid, "geometryParameters"))
    const [segmentedMeshMaterialAssignments] = scope.branch(scope.get(mattressMeshDataValid, "segmentedMeshMaterialAssignments"))

    const createEntry = (parameterName: MattressMeshGeometryParameterKey) =>
        scope.struct<EvaluatedTemplateInput>("geometryParametersLocalNodesInstanceInputs", {
            value: scope.get(geometryParametersDataValid, parameterName),
            type: "Number",
            ancestry: ["Number"],
        })

    const geometryParametersInputsData = scope.group<EvaluatedTemplateInputs>("geometryParametersLocalNodesInputsData", {
        [getLocalNodeIdForGeometryParameter("width")]: createEntry("width"),
        [getLocalNodeIdForGeometryParameter("length")]: createEntry("length"),
        [getLocalNodeIdForGeometryParameter("height")]: createEntry("height"),
        [getLocalNodeIdForGeometryParameter("cornerRadius")]: createEntry("cornerRadius"),
        [getLocalNodeIdForGeometryParameter("topRadius")]: createEntry("topRadius"),
        [getLocalNodeIdForGeometryParameter("bottomRadius")]: createEntry("bottomRadius"),
        [getLocalNodeIdForGeometryParameter("cornerRadiusSegments")]: createEntry("cornerRadiusSegments"),
        [getLocalNodeIdForGeometryParameter("topRadiusSegments")]: createEntry("topRadiusSegments"),
        [getLocalNodeIdForGeometryParameter("bottomRadiusSegments")]: createEntry("bottomRadiusSegments"),
        [getLocalNodeIdForGeometryParameter("widthSegments")]: createEntry("widthSegments"),
        [getLocalNodeIdForGeometryParameter("lengthSegments")]: createEntry("lengthSegments"),
        [getLocalNodeIdForGeometryParameter("heightSegments")]: createEntry("heightSegments"),
        [getLocalNodeIdForGeometryParameter("resolutionMultiplier")]: createEntry("resolutionMultiplier"),
    })

    const materialAssignmentInputsData = scope.pureLambda(
        segmentedMeshMaterialAssignments,
        (segmentedMeshMaterialAssignments) => {
            if (!segmentedMeshMaterialAssignments) return {}
            const inputsData: {[localNodeID: string]: EvaluatedTemplateInput} = {}
            Object.values(segmentedMeshMaterialAssignments).forEach((assignmentData) => {
                if (!assignmentData) return
                const {localMaterialInput, materialData} = assignmentData
                inputsData[localMaterialInput.parameters.id] = {
                    value: materialData,
                    type: "Material",
                    ancestry: ["Material"],
                }
            })
            return inputsData
        },
        "materialAssignmentInputsData",
    )

    return scope.pureLambda(
        scope.tuple(geometryParametersInputsData, materialAssignmentInputsData),
        ([geometryParametersInputsData, materialAssignmentInputsData]) => ({
            ...geometryParametersInputsData,
            ...materialAssignmentInputsData,
        }),
        "mattressMeshInstanceInputsData",
    )
}

// Compiles the segmented-mesh half of the mattress mesh data: derives per-cut segmentation info from
// the cuts data list, builds a segmented-mesh `MeshGeometryAttributeSetLambda` (the heavy attribute
// generator from mattress-mesh-segmentation), clones the segmented-mesh `GeometryGraphMesh` with its
// material-assignment slots populated, and reports material slot ownership + assignments for the
// surrounding Mattress.
function compileMattressSegmentedMeshData(
    scope: GraphBuilderScope,
    cutsData: BuilderOutlet<MattressCutData[]>,
    meshGeometryParametersLocalNodes: BuilderOutlet<MattressMeshGeometryParametersLocalNodes>,
    baseMeshLocalNodesData: BuilderOutlet<MattressMeshBaseMeshLocalNodes>,
    segmentedMeshLocalGraphNodesSrcData: BuilderOutlet<MattressMeshSegmentedMeshLocalNodes>,
): {
    localNodes: BuilderOutlet<MattressMeshSegmentedMeshLocalNodes | null>
    materialSlotsOwners: BuilderOutlet<MattressMeshData["segmentedMeshMaterialSlotsOwners"]>
    materialAssignments: BuilderOutlet<MattressMeshData["segmentedMeshMaterialAssignments"]>
} {
    const segmentationInfo_baseData = scope.const(
        () => ({
            materialID: baseMaterialID,
            texUVLayout: mattressFabricLayoutToTexUVLayout("BorderWithMirror"),
            // texUVLayout: "texUV_BorderWithMirror",
            materialAssignment: null,
        }),
        "segmentationInfo_baseData",
    )

    const segmentationInfo_curvesAndCutsData = scope.pureLambda(
        cutsData,
        (cutsData) => {
            const curveLocalNodeInstanceIdSet: Set<string> = new Set()
            const curveLocalNodeSet: Set<CurveLikeNode> = new Set()
            cutsData.forEach((cutData) =>
                cutData.conditions.forEach((condition) => {
                    if (condition.curveData.localNode) {
                        curveLocalNodeInstanceIdSet.add(condition.curveData.localNode.instanceId)
                        curveLocalNodeSet.add(condition.curveData.localNode)
                    }
                }),
            )

            if (curveLocalNodeSet.size !== curveLocalNodeInstanceIdSet.size) return null
            const curveList = Array.from(curveLocalNodeSet)

            const segmentationInfo_curvesData: {[curveID: string]: CurveLikeNode} = {}
            const getCurveID = (curve: CurveLikeNode) => {
                const curveID = curveList.findIndex((x) => x === curve)
                if (curveID === undefined) throw new Error("Missing cutting curve!")
                segmentationInfo_curvesData[`${curveID}`] = curveList[curveID]
                return `${curveID}`
            }

            const segmentationInfo_cutsData = cutsData.map((cutData, cutIdx) => ({
                ...cutData,
                materialID: getMaterialIDForCutIdx(cutIdx),
                conditions: cutData.conditions.map((condition) => ({side: condition.side, curveID: getCurveID(condition.curveData.localNode)})),
            }))

            return {
                curvesData: segmentationInfo_curvesData,
                cutsData: segmentationInfo_cutsData,
            }
        },
        "curvesAndCutsData",
    )

    const materialsData = scope.pureLambda(
        cutsData,
        (cutsData) => {
            const materialSlotsOwnersData: {[materialID: number]: MattressNodePath | null} = {}
            const materialAssignmentsData: {[materialID: number]: MattressMaterialAssignmentData | null} = {
                [baseMaterialID]: null,
            }
            for (let cutIdx = 0; cutIdx < cutsData.length; cutIdx++) {
                const materialID = getMaterialIDForCutIdx(cutIdx)
                materialSlotsOwnersData[materialID] = [...cutsData[cutIdx].path]
                materialAssignmentsData[materialID] = cutsData[cutIdx].materialAssignmentData
            }
            return {materialAssignmentsData, materialSlotsOwnersData}
        },
        "materialsData",
    )

    const [segmentationInfo_curvesAndCutsDataValid] = scope.branch(segmentationInfo_curvesAndCutsData)

    const segmentationInfo_hash = scope.pureLambda(
        segmentationInfo_curvesAndCutsDataValid,
        (curvesAndCutsData) => {
            const curvesDataHashData = (() => {
                const data: {[curveID: string]: string} = {}
                Object.keys(curvesAndCutsData.curvesData).forEach((curveID) => {
                    data[curveID] = curvesAndCutsData.curvesData[curveID].instanceId
                })
                return data
            })()

            const cutsDataHashData = curvesAndCutsData.cutsData.map((cutData) => ({
                materialID: cutData.materialID,
                conditions: cutData.conditions,
                texUVLayout: cutData.texUVLayout,
                materialAssignment: [
                    cutData.materialAssignmentData?.localNode?.parameters.horizontalOffset,
                    cutData.materialAssignmentData?.localNode?.parameters.verticalOffset,
                    cutData.materialAssignmentData?.localNode?.parameters.rotation,
                    cutData.materialAssignmentData?.localNode?.parameters.side,
                ],
            }))

            return hashObject({
                type: "segmentationInfo",
                curvesData: curvesDataHashData,
                cutsData: cutsDataHashData,
            })
        },
        "segmentationInfo_hash",
    )

    const segmentationInfo = scope.pureLambda(
        scope.tuple(
            segmentationInfo_baseData,
            scope.get(segmentationInfo_curvesAndCutsDataValid, "cutsData"),
            scope.get(segmentationInfo_curvesAndCutsDataValid, "curvesData"),
            segmentationInfo_hash,
        ),
        ([baseData, cutsData, curvesData, hash]) => ({
            baseData,
            cutsData,
            curvesData,
            changeDetectionHash: hash,
        }),
        "segmentationInfo",
        {output: SegmentationInfoTypeDescriptor},
    )

    const materialAssignmentsData = scope.get(materialsData, "materialAssignmentsData")
    const materialSlotsOwners = scope.get(materialsData, "materialSlotsOwnersData")

    const localNodes = scope.pureLambda(
        scope.tuple(segmentationInfo, meshGeometryParametersLocalNodes, segmentedMeshLocalGraphNodesSrcData, baseMeshLocalNodesData, materialAssignmentsData),
        ([segmentationInfo, meshGeometryParametersLocalNodes, segmentedMeshLocalGraphNodesSrcData, baseMeshLocalNodesData, materialAssignmentsData]) => {
            const attributes = segmentedMeshLocalGraphNodesSrcData.attributes.clone({
                cloneInstanceIds: true,
                cloneSubNode: () => false,
                parameterOverrides: {
                    ...getParametersForSegmentedMeshAttributesLocalNode(segmentationInfo, baseMeshLocalNodesData.mesh, meshGeometryParametersLocalNodes),
                },
            })

            const materialAssignments: {[materialID: string]: MaterialAssignment | null} = {}
            Object.keys(materialAssignmentsData).forEach((materialID) => {
                materialAssignments[materialID] = materialAssignmentsData[parseInt(materialID)]?.localNode ?? null
            })

            const mesh = segmentedMeshLocalGraphNodesSrcData.mesh.clone({
                cloneInstanceIds: true,
                cloneSubNode: () => false,
                parameterOverrides: {
                    meshAttributeSet: attributes,
                    materialAssignments: new MaterialAssignments({parameters: {...materialAssignments}}),
                },
            })

            const exportNode = segmentedMeshLocalGraphNodesSrcData.export.clone({
                cloneInstanceIds: true,
                cloneSubNode: () => false,
                parameterOverrides: {
                    node: mesh,
                },
            })

            return {
                attributes,
                mesh,
                export: exportNode,
            }
        },
        "segmentedMeshLocalNodesData",
    )

    return {
        localNodes: scope.phi(localNodes, null),
        materialSlotsOwners: scope.phi(materialSlotsOwners, null),
        materialAssignments: scope.phi(materialAssignmentsData, null),
    }
}

// Completes the mattress mesh data by adding the segmented-mesh half (the cut-aware variant produced
// by `compileMattressSegmentedMeshData`) on top of the partial data published by
// `MattressMesh.onCompile` via `aliasMattressMeshData`.
export function evaluateFullMattressMeshData(
    scope: GraphBuilderScope,
    evaluator: NodeEvaluator,
    cutsData: BuilderOutlet<MattressCutData[]>,
    partialMattressMeshData: BuilderOutlet<MattressMeshData | null>,
): BuilderOutlet<MattressMeshData | null> {
    const templateScope = evaluator.templateScope

    const segmentedMeshLocalNodesSrc = scope.const<MattressMeshSegmentedMeshLocalNodes>(
        () => ({
            attributes: new MeshGeometryAttributeSetLambda({
                parameters: {
                    name: "",
                    args: null,
                    generator: null,
                    // the cutting-curve projection is too expensive to rerun on every control point drag step
                    deferWhileModifyingScene: true,
                },
            }),
            mesh: new GeometryGraphMesh({
                parameters: {
                    name: MATTRESS_LOCAL_NODE_FOR_SEGMENTED_MESH,
                    meshAttributeSet: null,
                    materialAssignments: new MaterialAssignments({parameters: {}}),
                    materialSlotNames: {},
                    lockedTransform: Matrix4.identity().toArray(),
                    visible: true,
                    visibleDirectly: true,
                    visibleInReflections: true,
                    visibleInRefractions: true,
                    castRealtimeShadows: true,
                    receiveRealtimeShadows: true,
                },
            }),
            export: new MeshExport({
                parameters: {
                    name: "",
                    node: null,
                    id: uuid4(),
                },
            }),
        }),
        "segmentedMeshLocalNodesSrc",
    )

    const [partialMattressMeshDataValid] = scope.branch(partialMattressMeshData)
    const [geometryParametersLocalNodes] = scope.branch(scope.get(partialMattressMeshDataValid, "geometryParametersLocalNodes"))
    const [baseMeshLocalNodes] = scope.branch(scope.get(partialMattressMeshDataValid, "baseMeshLocalNodes"))

    const {
        localNodes: segmentedMeshLocalNodes,
        materialSlotsOwners: segmentedMeshMaterialSlotsOwners,
        materialAssignments: segmentedMeshMaterialAssignments,
    } = compileMattressSegmentedMeshData(templateScope, cutsData, geometryParametersLocalNodes, baseMeshLocalNodes, segmentedMeshLocalNodesSrc)

    return scope.struct<MattressMeshData>("mattressMeshData", {
        geometryParameters: scope.get(partialMattressMeshDataValid, "geometryParameters"),
        geometryParametersLocalNodes,
        geometryParameterSourceNodes: scope.get(partialMattressMeshDataValid, "geometryParameterSourceNodes"),
        baseMeshLocalNodes,
        segmentedMeshLocalNodes,
        segmentedMeshMaterialSlotsOwners,
        segmentedMeshMaterialAssignments,
    })
}
